import { Options } from '@angular-slider/ngx-slider';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Constants from 'src/app/core/constants/constants';
import { ISite } from 'src/app/core/interface';
import { RestAPIsService } from 'src/app/core/services/rest-apis.service';
import { SessionService } from 'src/app/core/services/session.service';
import { UtilService } from 'src/app/core/services/util.service';

@Component({
  selector: 'app-edit-assumption',
  templateUrl: './edit-assumption.component.html',
  styleUrls: ['./edit-assumption.component.scss'],
})
export class EditAssumptionComponent implements OnInit {
  @Input() siteData!: ISite;
  @Output() onClose = new EventEmitter<boolean>()
  assumptionForm: FormGroup;
  options: Options = {
    floor: 0,
    ceil: 100,
    animate: true,
    showSelectionBar: true,
    disabled: false,
    getSelectionBarColor: (value: number): string => { return '#90C7CF'; },
    getPointerColor: (value: number): string => { return '#90C7CF'; },
  };
  successMessage = ''
  errorMessage = ''
  assumptionData:any;
  cateringElec: number = 0;
  heatingElec: number = 0;
  hotwaterElec: number = 0;
  lighting: number = 0;
  cateringGas: number = 0;
  heatingGas: number = 0;
  hotwaterGas: number = 0;
  others: number = 0;
  elecErrorMessage: string = '';
  gasErrorMessage: string = '';
  source = '';
  threeRateCoverage: { percentage: number, gapMinutes: number, gaps: { start: string, end: string }[], overlapMinutes: number, overlaps: { start: string, end: string, periods: string }[] } = { percentage: 100, gapMinutes: 0, gaps: [], overlapMinutes: 0, overlaps: [] };

  private timeToMinutes(timeObj: { hour: number, minute: number }): number {
    if (!timeObj) return 0;
    return timeObj.hour * 60 + timeObj.minute;
  }

  private markPeriod(counts: number[], start: number, end: number): void {
    if (start === end) return;
    if (start < end) {
      for (let i = start; i < end; i++) counts[i]++;
    } else {
      for (let i = start; i < 1440; i++) counts[i]++;
      for (let i = 0; i < end; i++) counts[i]++;
    }
  }

  private findRanges(arr: boolean[]): { start: string, end: string }[] {
    const ranges: { start: string, end: string }[] = [];
    let inRange = false;
    let rangeStart = 0;
    for (let i = 0; i < 1440; i++) {
      if (arr[i] && !inRange) {
        inRange = true;
        rangeStart = i;
      } else if (!arr[i] && inRange) {
        inRange = false;
        ranges.push({ start: this.minutesToTimeString(rangeStart), end: this.minutesToTimeString(i) });
      }
    }
    if (inRange) {
      ranges.push({ start: this.minutesToTimeString(rangeStart), end: '00:00' });
    }
    return ranges;
  }

  private minutesToTimeString(minutes: number): string {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return h.toString().padStart(2, '0') + ':' + m.toString().padStart(2, '0');
  }

  updateThreeRateCoverage(): void {
    const weekdayStart = this.timeToMinutes(this.assumptionForm.get('elecWeekdayStart')?.value);
    const weekdayEnd = this.timeToMinutes(this.assumptionForm.get('elecWeekdayEnd')?.value);
    const eveningStart = this.timeToMinutes(this.assumptionForm.get('elecEveningStart')?.value);
    const eveningEnd = this.timeToMinutes(this.assumptionForm.get('elecEveningEnd')?.value);

    const counts = new Array(1440).fill(0);
    this.markPeriod(counts, weekdayStart, weekdayEnd);
    this.markPeriod(counts, eveningStart, eveningEnd);
    this.markPeriod(counts, eveningEnd, weekdayStart);

    const gapArr = counts.map((c: number) => c === 0);
    const overlapArr = counts.map((c: number) => c > 1);
    const coveredMinutes = counts.filter((c: number) => c > 0).length;
    const percentage = Math.round((coveredMinutes / 1440) * 100);
    const gapMinutes = 1440 - coveredMinutes;
    const overlapMinutes = counts.filter((c: number) => c > 1).length;
    const gaps = this.findRanges(gapArr);

    // Build overlap ranges with period labels
    const overlapRanges = this.findRanges(overlapArr);
    const overlaps = overlapRanges.map(r => {
      const midMin = this.parseTimeStringToMinutes(r.start);
      const periods: string[] = [];
      if (this.isMinuteInPeriod(midMin, weekdayStart, weekdayEnd)) periods.push('Weekday');
      if (this.isMinuteInPeriod(midMin, eveningStart, eveningEnd)) periods.push('Evening');
      if (this.isMinuteInPeriod(midMin, eveningEnd, weekdayStart)) periods.push('Night');
      return { ...r, periods: periods.join(' & ') };
    });

    this.threeRateCoverage = { percentage, gapMinutes, gaps, overlapMinutes, overlaps };
  }

  private parseTimeStringToMinutes(time: string): number {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  }

  private isMinuteInPeriod(minute: number, start: number, end: number): boolean {
    if (start === end) return false;
    if (start < end) return minute >= start && minute < end;
    return minute >= start || minute < end;
  }

  // Validator to ensure time is afternoon/evening (12:00 or later)
  private afternoonEveningTimeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      const timeObj = control.value;
      if (timeObj.hour < 12) {
        return { 'notAfternoonEvening': true };
      }
      return null;
    };
  }

  // Convert {hour, minute} to "HH:mm" string
  private timeObjToString(timeObj: {hour: number, minute: number}): string {
    if (!timeObj) return '00:00';
    return timeObj.hour.toString().padStart(2, '0') + ':' + timeObj.minute.toString().padStart(2, '0');
  }

  // Convert "HH:mm" string to {hour, minute} object
  private parseTimeString(time: string): {hour: number, minute: number} {
    if (!time) return {hour: 0, minute: 0};
    const [h, m] = time.split(':').map(Number);
    return {hour: h, minute: m};
  }

  // For template display
  formatTime(val: any): string {
    if (!val) return '--:--';
    if (typeof val === 'string') return val;
    return this.timeObjToString(val);
  }

  constructor(private modalService: NgbModal, private sessionService: SessionService,
    private restAPI: RestAPIsService, private spinner: NgxSpinnerService,
    private toaster: ToastrService, private util: UtilService
  ) {
    this.source = this.util.source
    this.assumptionForm = new FormGroup({
      // Electricity Rate Configuration - Primary Choice
      elecRateTypeMain: new FormControl('single', Validators.required),
      
      // Electricity Rate Configuration - Multi-Rate Sub-Choice
      elecRateTypeMulti: new FormControl('dayNight', Validators.required),
      
      // Single Rate
      unitRate: new FormControl(),
      elecStandingCharge: new FormControl(),
      
      // Day-Night Rate
      elecDayRate: new FormControl(),
      elecNightRate: new FormControl(),
      elecDayStartTime: new FormControl({hour: 7, minute: 0}),
      elecDayEndTime: new FormControl({hour: 23, minute: 0}),
      elecStandingChargeDayNight: new FormControl(),
      
      // Three-Rate
      elecWeekdayRate: new FormControl(),
      elecEveningRate: new FormControl(),
      elecNightRateThree: new FormControl(),
      elecWeekdayStart: new FormControl({hour: 7, minute: 0}),
      elecWeekdayEnd: new FormControl({hour: 16, minute: 0}),
      elecEveningStart: new FormControl({hour: 16, minute: 0}, this.afternoonEveningTimeValidator()),
      elecEveningEnd: new FormControl({hour: 23, minute: 0}, this.afternoonEveningTimeValidator()),
      elecStandingChargeThreeRate: new FormControl(),
      
      // Two-Rate
      elecTwoWeekdayRate: new FormControl(),
      elecTwoEveningRate: new FormControl(),
      elecTwoWeekdayStart: new FormControl({hour: 7, minute: 0}),
      elecTwoWeekdayEnd: new FormControl({hour: 19, minute: 0}),
      elecStandingChargeTwoRate: new FormControl(),
      
      // Existing fields
      elecUnitRate: new FormControl(),
      gasUnitRate: new FormControl(),
      gasStandingCharge: new FormControl(),
      ledPercent: new FormControl(),
      heatingSource: new FormControl(),

      cateringElec: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      heatingElec: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      hotwaterElec: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      lighting: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),

      cateringGas: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      heatingGas: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      hotwaterGas: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      others: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
    })
    this.assumptionForm.valueChanges.subscribe(values => {
      if (values.elecRateTypeMain === 'multi' && values.elecRateTypeMulti === 'threeRate') {
        this.updateThreeRateCoverage();
      }
      const elecTotal = (values.cateringElec || 0) + (values.heatingElec || 0) + (values.hotwaterElec || 0) + (values.lighting || 0)+ (values.others || 0);
      if(elecTotal != 100){
        this.elecErrorMessage = `Total Electricity consumption splits should be equal to 100%, Currently it's ${elecTotal}%`;
        this.assumptionForm.get('cateringElec')?.setErrors({invalid: true})
        this.assumptionForm.get('heatingElec')?.setErrors({invalid: true})
        this.assumptionForm.get('hotwaterElec')?.setErrors({invalid: true})
        this.assumptionForm.get('lighting')?.setErrors({invalid: true})
        this.assumptionForm.get('others')?.setErrors({invalid: true})
      }
      else{
        this.elecErrorMessage = ''
        this.assumptionForm.get('cateringElec')?.setErrors(null);
        this.assumptionForm.get('heatingElec')?.setErrors(null)
        this.assumptionForm.get('hotwaterElec')?.setErrors(null)
        this.assumptionForm.get('lighting')?.setErrors(null)
        this.assumptionForm.get('others')?.setErrors(null)
      }  
      if(this.assumptionData?.mprn?.length){
        const gasTotal = (values.cateringGas || 0) + (values.heatingGas || 0) + (values.hotwaterGas || 0);
        if(gasTotal != 100){
          this.gasErrorMessage = `Total Gas consumption splits should be equal to 100%, Currently it's ${gasTotal}%`;
          this.assumptionForm.get('cateringGas')?.setErrors({invalid: true})
          this.assumptionForm.get('heatingGas')?.setErrors({invalid: true})
          this.assumptionForm.get('hotwaterGas')?.setErrors({invalid: true})          
        }
        else{
          this.gasErrorMessage = ''
          this.assumptionForm.get('cateringGas')?.setErrors(null);
          this.assumptionForm.get('heatingGas')?.setErrors(null)
          this.assumptionForm.get('hotwaterGas')?.setErrors(null)          
        }        
      }
    });    
  }  

  ngOnInit(): void {
    if(this.assumptionData){
      const elecRateConfig = this.assumptionData.energy?.electricityRateConfig;
      
      // Backwards compatibility: if old elecUnitRate exists, treat as single rate
      if (elecRateConfig && this.source == 'edf') {
        // New format
        this.assumptionForm.patchValue({
          elecRateTypeMain: elecRateConfig.rateType === 'single' ? 'single' : 'multi',
        });
        
        if (elecRateConfig.rateType === 'single' && elecRateConfig.single) {
          this.assumptionForm.patchValue({
            unitRate: (elecRateConfig.single.unitRate || this.assumptionData.energy.elecUnitRate.value) * 100,
            elecStandingCharge: elecRateConfig.single.standingCharge,
          });
        } else if (elecRateConfig.rateType === 'dayNight' && elecRateConfig.dayNight) {
          this.assumptionForm.patchValue({
            elecRateTypeMulti: 'dayNight',
            elecDayRate: elecRateConfig.dayNight.dayRate,
            elecNightRate: elecRateConfig.dayNight.nightRate,
            elecDayStartTime: this.parseTimeString(elecRateConfig.dayNight.dayStartTime),
            elecDayEndTime: this.parseTimeString(elecRateConfig.dayNight.dayEndTime),
            elecStandingChargeDayNight: elecRateConfig.dayNight.standingCharge,
          });
        } else if (elecRateConfig.rateType === 'threeRate' && elecRateConfig.threeRate) {
          this.assumptionForm.patchValue({
            elecRateTypeMulti: 'threeRate',
            elecWeekdayRate: elecRateConfig.threeRate.weekdayRate,
            elecEveningRate: elecRateConfig.threeRate.eveningRate,
            elecNightRateThree: elecRateConfig.threeRate.nightRate,
            elecWeekdayStart: this.parseTimeString(elecRateConfig.threeRate.weekdayStart),
            elecWeekdayEnd: this.parseTimeString(elecRateConfig.threeRate.weekdayEnd),
            elecEveningStart: this.parseTimeString(elecRateConfig.threeRate.eveningStart),
            elecEveningEnd: this.parseTimeString(elecRateConfig.threeRate.eveningEnd),
            elecStandingChargeThreeRate: elecRateConfig.threeRate.standingCharge,
          });
        } else if (elecRateConfig.rateType === 'twoRate' && elecRateConfig.twoRate) {
          this.assumptionForm.patchValue({
            elecRateTypeMulti: 'twoRate',
            elecTwoWeekdayRate: elecRateConfig.twoRate.weekdayRate,
            elecTwoEveningRate: elecRateConfig.twoRate.eveningRate,
            elecTwoWeekdayStart: this.parseTimeString(elecRateConfig.twoRate.weekdayStart),
            elecTwoWeekdayEnd: this.parseTimeString(elecRateConfig.twoRate.weekdayEnd),
            elecStandingChargeTwoRate: elecRateConfig.twoRate.standingCharge,
          });
        }
      } else if (this.assumptionData.energy?.elecUnitRate) {
        // Old format - backwards compatibility
        this.assumptionForm.patchValue({
          // elecRateTypeMain: 'single',
          elecUnitRate: this.assumptionData.energy.elecUnitRate.value * 100,
          // elecStandingCharge: 0, // Default for migrated data
        });
      }

      if(this.source == 'edf'){
        this.assumptionForm.patchValue({
          gasStandingCharge: this.assumptionData.energy.gasStandingCharge?.value * 100 || 0,
        })
      }
      
      this.assumptionForm.patchValue({
        gasUnitRate: this.assumptionData.energy.gasUnitRate.value*100,
        ledPercent: this.assumptionData.lighting.ledPercent.value,
        heatingSource: this.assumptionData.building.heatingSource.value,
        cateringElec: this.assumptionData.energy?.split.cateringElec.value,
        heatingElec: this.assumptionData.energy?.split.heatingElec.value,
        hotwaterElec: this.assumptionData.energy?.split.hotwaterElec.value,
        lighting: this.assumptionData.energy?.split.lighting.value,
        cateringGas: this.assumptionData.energy?.split.cateringGas.value,
        heatingGas: this.assumptionData.energy?.split.heatingGas.value,
        hotwaterGas: this.assumptionData.energy?.split.hotwaterGas.value,
        others: this.assumptionData.energy?.split.others.value,
      })
    }
    this.updateThreeRateCoverage();
  }
  close(reload = false): void {
    this.onClose.emit(reload);
    this.modalService.dismissAll();
  }

  updateAssumptions(): void{
    const companySiteData = this.sessionService.getItemFromSession(Constants.sessionConstants.COMPANY_SITE_DATA)
    this.successMessage = ''
    this.errorMessage = ''
    const formValues = {...this.assumptionForm.value}
    
    // Build electricity rate configuration
    const electricityRate: any = {};
    const elecRateTypeMain = formValues.elecRateTypeMain;
    const elecRateTypeMulti = formValues.elecRateTypeMulti;
    
    if(this.source == 'edf'){
      if (elecRateTypeMain === 'single') {
        electricityRate.rateType = 'single';
        electricityRate.single = {
          unitRate: formValues.unitRate/100,
          standingCharge: formValues.elecStandingCharge,
        };
      } else if (elecRateTypeMain === 'multi') {
        if (elecRateTypeMulti === 'dayNight') {
          electricityRate.rateType = 'dayNight';
          electricityRate.dayNight = {
            dayRate: formValues.elecDayRate,
            nightRate: formValues.elecNightRate,
            dayStartTime: this.timeObjToString(formValues.elecDayStartTime),
            dayEndTime: this.timeObjToString(formValues.elecDayEndTime),
            standingCharge: formValues.elecStandingChargeDayNight,
          };
        } else if (elecRateTypeMulti === 'threeRate') {
          electricityRate.rateType = 'threeRate';
          electricityRate.threeRate = {
            weekdayRate: formValues.elecWeekdayRate,
            eveningRate: formValues.elecEveningRate,
            nightRate: formValues.elecNightRateThree,
            weekdayStart: this.timeObjToString(formValues.elecWeekdayStart),
            weekdayEnd: this.timeObjToString(formValues.elecWeekdayEnd),
            eveningStart: this.timeObjToString(formValues.elecEveningStart),
            eveningEnd: this.timeObjToString(formValues.elecEveningEnd),
            standingCharge: formValues.elecStandingChargeThreeRate,
          };
        } else if (elecRateTypeMulti === 'twoRate') {
          electricityRate.rateType = 'twoRate';
          electricityRate.twoRate = {
            weekdayRate: formValues.elecTwoWeekdayRate,
            eveningRate: formValues.elecTwoEveningRate,
            weekdayStart: this.timeObjToString(formValues.elecTwoWeekdayStart),
            weekdayEnd: this.timeObjToString(formValues.elecTwoWeekdayEnd),
            standingCharge: formValues.elecStandingChargeTwoRate,
          };
        }
      }
    }
    
    const payload:any = {
      gasUnitRate: formValues.gasUnitRate/100,
      ledPercent: formValues.ledPercent,
      heatingSource: formValues.heatingSource,
      energySplit: {
        cateringElec: formValues.cateringElec,
        heatingElec: formValues.heatingElec,
        hotwaterElec: formValues.hotwaterElec,
        lighting: formValues.lighting,
        others: formValues.others
      },      
      email: companySiteData?.contactEmail,
      siteId: this.siteData.siteId,
    }
    if(this.source == 'edf'){
      payload['electricityRateConfig'] = electricityRate;
      payload['gasStandingCharge'] = formValues.gasStandingCharge/100;
    }
    else{
      payload['elecUnitRate'] = formValues.elecUnitRate/100;
    }
    if(this.assumptionData?.mprn?.length){
      payload['energySplit']['cateringGas'] = formValues.cateringGas;
      payload['energySplit']['heatingGas'] = formValues.heatingGas;
      payload['energySplit']['hotwaterGas'] = formValues.hotwaterGas;
    }
    this.spinner.show()
    this.restAPI.postAPI('carbon/v3/update-site-assumptions', payload, 'domestic').subscribe({
      next: (res) => {
        this.restAPI.addCheckpoint({event: 'EDIT_ASSUMPTION'})
        if (res?.status === 'Success') {
          this.close(true)
          this.toaster.success('Assumption update successfully')
          this.assumptionData = res.data
        }
        else this.errorMessage = res.data
        this.spinner.hide();
      }, error: (err) => {
        console.log('ERRORS: ', err);
        this.spinner.hide();
      }
    })
  }
}
